/**
 * Chat domain types matching backend roboto.ai.chat module
 */

/**
 * Enumeration of possible roles in a chat conversation.
 */
export enum ChatRole {
  USER = "user",
  ASSISTANT = "assistant",
  ROBOTO = "roboto",
}

/**
 * Enumeration of possible message generation states.
 */
export enum ChatMessageStatus {
  NOT_STARTED = "not_started",
  GENERATING = "generating",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled",
}

export enum ChatStatus {
  NOT_STARTED = "not_started",
  USER_TURN = "user_turn",
  ROBOTO_TURN = "roboto_turn",
}

/**
 * Enumeration of different types of content within chat messages.
 */
export enum ChatContentType {
  TEXT = "text",
  TOOL_USE = "tool_use",
  TOOL_RESULT = "tool_result",
  ERROR = "error",
}

/**
 * Text content within a chat message.
 */
export interface ChatTextContent {
  text: string;
}

export const isChatTextContent = (obj: unknown): obj is ChatTextContent => {
  return typeof obj === "object" && obj !== null && "text" in obj;
};

/**
 * Tool usage request content within a chat message.
 */
export interface ChatToolUseContent {
  content_type: "tool_use";
  tool_name: string;
  tool_use_id: string;
  raw_request?: Record<string, unknown>;
}

export const isChatToolUseContent = (
  obj: unknown,
): obj is ChatToolUseContent => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "content_type" in obj &&
    obj.content_type === "tool_use"
  );
};

/**
 * Tool execution result content within a chat message.
 */
export interface ChatToolResultContent {
  content_type: "tool_result";
  tool_name: string;
  tool_use_id: string;
  runtime_ms: number;
  status: string;
  raw_response?: Record<string, unknown>;
}

export const isChatToolResultContent = (
  obj: unknown,
): obj is ChatToolResultContent => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "content_type" in obj &&
    obj.content_type === "tool_result"
  );
};

export interface ChatToolDetailResponse {
  tool_use: ChatToolUseContent;
  tool_result: ChatToolResultContent;
}

/**
 * Error content within a chat message.
 * Used when message generation fails due to an error or is cancelled by the user.
 */
export interface ChatErrorContent {
  content_type: "error";
  /** User-friendly error message describing what went wrong. */
  error_message: string;
  /** Optional error code for programmatic handling. */
  error_code?: string;
}

export const isChatErrorContent = (obj: unknown): obj is ChatErrorContent => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "content_type" in obj &&
    obj.content_type === "error"
  );
};

/**
 * Type alias for all possible content types within chat messages.
 */
export type ChatContent =
  | ChatTextContent
  | ChatToolUseContent
  | ChatToolResultContent
  | ChatErrorContent;

/**
 * Contextual information for AI model generation.
 */
export interface RobotoLLMContext {
  /**
   * List of dataset IDs to consider for AI model generation.
   */
  dataset_ids: string[];

  /**
   * List of file IDs to consider for AI model generation.
   */
  file_ids: string[];

  /**
   * Optional layout of the visualizer to consider for AI model generation.
   */
  visualizer_state?: Record<string, unknown>;

  /**
   * Optional miscellaneous context to consider for AI model generation.
   */
  misc_context?: Record<string, unknown>;
}

/**
 * A single message within a chat conversation.
 */
export interface ChatMessage {
  /** The role of the message sender (user, assistant, or roboto). */
  role: ChatRole;

  /** List of content blocks that make up this message. */
  content: ChatContent[];

  /** Current generation status of this message. */
  status?: ChatMessageStatus;

  /** ISO timestamp when this message was created. */
  created?: string;
}

/**
 * Complete record of a chat session.
 */
export interface ChatRecord {
  /** Unique identifier for this chat session. */
  chat_id: string;

  /** Timestamp when the chat session was created (ISO string). */
  created: string;

  /** User ID of the person who created this chat session. */
  created_by: string;

  /** Organization ID that owns this chat session. */
  org_id: string;

  /** Complete list of messages in the conversation. */
  messages: ChatMessage[];

  /** Token used for incremental updates and synchronization. */
  continuation_token: string;

  /** Current status of the chat session. */
  status: ChatStatus;

  /** Optional title for the chat session. */
  title?: string;
}

/**
 * Incremental update to a chat session.
 */
export interface ChatRecordDelta {
  /** New or updated messages indexed by their position in the conversation. */
  messages_by_idx: Record<number, ChatMessage>;

  /** Updated token for the next incremental synchronization. */
  continuation_token: string;

  /** Updated status of the chat session. */
  status?: ChatStatus;

  /** Updated title of the chat session. */
  title?: string;
}

/**
 * Request payload for starting a new chat session.
 */
export interface StartChatRequest {
  /** Optional context to consider for AI model generation. */
  context?: RobotoLLMContext;

  /** Initial messages to start the conversation with. */
  messages: ChatMessage[];

  /** Optional system prompt to customize AI assistant behavior. */
  system_prompt?: string;
}

/**
 * Request payload for sending a new message in a chat session.
 */
export interface SendMessageRequest {
  /** Optional context to consider for AI model generation. */
  context?: RobotoLLMContext;
  /** Message to send */
  message: ChatMessage;
}
